import type React from 'react';

import { useEffect } from 'react';

import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';

import { BrowserSize } from '../../../../../utility/browser';
import CartErrorNotice from '../../../../cart/components/molecules/CartErrorNotice';
import { useFromBrowserMediaQuery } from '../../../../common/components/atoms/ResponsiveBreakpoints';
import OriginalSearchTerm from '../../../../search/components/OriginalSearchTerm';
import { useProductSearchStateContext } from '../../../../search/hooks/useProductSearchStateContext';
import { useSearchOverlayMaxHeightCalculation } from '../../../../search/hooks/useSearchOverlayMaxHeightCalculation';
import ecommerceSearchOverlayStyles from '../EcommerceSearchOverlay/EcommerceSearchOverlay.module.scss';
import { getEcommerceSearchOverlayResultsMessage } from '../EcommerceSearchOverlay/EcommerceSearchOverlay.utils';
import EcommerceSearchOverlayResultsSummary from '../EcommerceSearchOverlay/EcommerceSearchOverlayResultsSummary';
import { useSearchOverlayAnnounce } from '../SearchOverlay/SearchOverlay.hooks';
import SearchOverlayActions from '../SearchOverlay/SearchOverlayActions';
import SearchOverlayFooter from '../SearchOverlay/SearchOverlayFooter';
import SearchOverlayFooterButton from '../SearchOverlay/SearchOverlayFooterButton';
import CheckoutOriginalSearchTermCta from './CheckoutOverlayOriginalSearchTermCta';
import CheckoutOverlayResultList from './CheckoutOverlayResultList';

interface CheckoutSearchOverlayResultsProps {
    onFullResultsClick: () => void;
    onHintClick: () => void;
    onDidYouMeanClick: () => void;
    onItemAddToCartClick: (item: ApiProduct, index: number) => void;
    onItemRemoveFromCartClick: (item: ApiProduct, index: number) => void;
    onScroll: React.UIEventHandler<HTMLDivElement>;
    useMaxHeightCalculation: boolean;
}

const CheckoutSearchOverlayResults = (
    props: React.PropsWithChildren<CheckoutSearchOverlayResultsProps>,
) => {
    const { setQuery, setSearchType, result, queryUsed, clear } = useProductSearchStateContext();

    const isFromLarge = useFromBrowserMediaQuery(BrowserSize.lg);

    const { contentWrapperRef, footerElementRef, listMaxHeight, recalculateMaxHeight } =
        useSearchOverlayMaxHeightCalculation(isFromLarge ? 100 : 0, props.useMaxHeightCalculation);

    useEffect(() => {
        recalculateMaxHeight();
    }, [result, recalculateMaxHeight]);

    const hasResults = !!result && result.items.length > 0;

    useSearchOverlayAnnounce((_visibleResults, totalResults, _queryUsed) =>
        getEcommerceSearchOverlayResultsMessage(_queryUsed!, totalResults),
    );

    return (
        <>
            <CartErrorNotice fixed />
            <div
                ref={contentWrapperRef}
                className={props.useMaxHeightCalculation ? 'mixinScroll' : undefined}
                onScroll={props.onScroll}
                style={
                    // eslint-disable-next-line no-nested-ternary
                    props.useMaxHeightCalculation
                        ? !isFromLarge && result
                            ? {
                                  height: `${listMaxHeight}px`,
                              }
                            : {
                                  maxHeight: `${listMaxHeight}px`,
                              }
                        : undefined
                }
            >
                {hasResults && (
                    <div
                        className={ecommerceSearchOverlayStyles.EcommerceSearchOverlayResultsHeader}
                    >
                        {queryUsed && (
                            <EcommerceSearchOverlayResultsSummary
                                queryUsed={queryUsed}
                                totalResults={result.totalResults}
                            />
                        )}
                        <SearchOverlayActions onHintClick={props.onHintClick}>
                            {(_hasDifferentQueriesUsed, _queryUsed) =>
                                _hasDifferentQueriesUsed && (
                                    <OriginalSearchTerm
                                        queryUsed={_queryUsed}
                                        originalQuery={result?.query}
                                        containerClass={
                                            ecommerceSearchOverlayStyles[
                                                'EcommerceSearchOverlayResults-OriginalSearchTerm'
                                            ]
                                        }
                                        renderLink={(originalQuery) => (
                                            <CheckoutOriginalSearchTermCta
                                                queryUsed={_queryUsed}
                                                originalQuery={originalQuery}
                                                onButtonClick={() => {
                                                    setSearchType('default');
                                                    setQuery(originalQuery);
                                                    props.onDidYouMeanClick?.();
                                                }}
                                            />
                                        )}
                                    />
                                )
                            }
                        </SearchOverlayActions>
                    </div>
                )}
                <CheckoutOverlayResultList
                    onFullResultsClick={props.onFullResultsClick}
                    onHintClick={props.onHintClick}
                    onItemAddToCartClick={props.onItemAddToCartClick}
                    onItemRemoveFromCartClick={props.onItemRemoveFromCartClick}
                />
            </div>
            <SearchOverlayFooter ref={footerElementRef}>
                <SearchOverlayFooterButton as="button" onClick={clear}>
                    Tillbaka till kassan
                </SearchOverlayFooterButton>
            </SearchOverlayFooter>
        </>
    );
};

export default CheckoutSearchOverlayResults;
